import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Camera, ArrowLeft, CheckCircle2, XCircle, User } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { staffAPI } from '../../services/api';
import { selfieAttendanceAPI } from '../../services/api';

export default function SelfieAttendanceHistory() {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    staffAPI.list({ status: 'ACTIVE' }).then((res) => {
      setStaffList(res.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedStaff) {
      setRecords([]);
      return;
    }
    setLoading(true);
    selfieAttendanceAPI.getHistory(selectedStaff)
      .then((res) => setRecords(res.data || []))
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, [selectedStaff]);

  const formatTime = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (d) => {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  };

  const totalDayHours = (inTime, outTime) => {
    if (!inTime || !outTime) return '—';
    const a = new Date(inTime).getTime();
    const b = new Date(outTime).getTime();
    if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return '—';
    const mins = Math.round((b - a) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <Calendar className="h-7 w-7 text-teal-600" />
              Selfie Attendance History
            </h1>
            <p className="text-ga-text-muted mt-1">Per-staff daily IN/OUT — use Selfie records for all staff.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/dashboard/selfie-attendance/records"
              className="px-4 py-2 rounded-lg border border-ga-border-subtle text-ga-text-primary text-sm font-medium
                         hover:bg-ga-bg-surface-subtle/40 transition"
            >
              All records
            </Link>
            <Link
              to="/dashboard/selfie-attendance"
              className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium
                         hover:bg-teal-700 transition flex items-center gap-2"
            >
              <Camera className="h-4 w-4" />
              Take Selfie
            </Link>
          </div>
        </div>

        <div className="ga-card rounded-xl shadow-sm border p-4">
          <label className="block text-sm font-medium text-ga-text-primary mb-1">Staff Member</label>
          <select
            value={selectedStaff}
            onChange={(e) => setSelectedStaff(e.target.value)}
            className="ga-input w-full px-3 py-2 rounded-lg"
          >
            <option value="">-- Select staff member --</option>
            {staffList.map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="py-12 text-center text-ga-text-muted">Loading...</div>
        ) : selectedStaff && records.length === 0 ? (
          <div className="ga-card rounded-xl shadow-sm border py-12 text-center text-ga-text-muted">
            No selfie attendance records found.
          </div>
        ) : (
          <div className="space-y-3">
            {records.map((rec) => (
              <div
                key={rec.id}
                className="ga-card rounded-xl shadow-sm border p-4 flex items-start gap-4"
              >
                {/* Date column */}
                <div className="text-center min-w-[60px]">
                  <div className="text-lg font-bold text-ga-text-primary">
                    {new Date(rec.date + 'T00:00:00').getDate()}
                  </div>
                  <div className="text-xs text-ga-text-muted uppercase">
                    {new Date(rec.date + 'T00:00:00').toLocaleDateString('en-IN', { month: 'short' })}
                  </div>
                  <div className="text-[10px] text-ga-text-muted">
                    {new Date(rec.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short' })}
                  </div>
                </div>

                {/* Times */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="text-xs text-ga-text-muted">IN</span>
                      <span className="text-sm font-semibold text-ga-text-primary">{formatTime(rec.in_time)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 rounded-full bg-red-400" />
                      <span className="text-xs text-ga-text-muted">OUT</span>
                      <span className="text-sm font-semibold text-ga-text-primary">{formatTime(rec.out_time)}</span>
                    </div>
                    <div className="text-xs text-ga-text-muted pt-1">
                      Total (day):{' '}
                      <span className="font-medium text-ga-text-primary">{totalDayHours(rec.in_time, rec.out_time)}</span>
                    </div>
                  </div>

                  {/* Checks summary */}
                  <div className="flex gap-3 flex-wrap">
                    {rec.in_checks_passed && (
                      <div className="flex items-center gap-1">
                        {Object.values(rec.in_checks_passed).every(Boolean) ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5 text-amber-500" />
                        )}
                        <span className="text-[11px] text-ga-text-muted">IN verified</span>
                      </div>
                    )}
                    {rec.out_checks_passed && (
                      <div className="flex items-center gap-1">
                        {Object.values(rec.out_checks_passed).every(Boolean) ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5 text-amber-500" />
                        )}
                        <span className="text-[11px] text-ga-text-muted">OUT verified</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Photo thumbnails */}
                <div className="flex gap-2">
                  {rec.in_photo_url ? (
                    <img
                      src={rec.in_photo_url}
                      alt="IN"
                      className="w-10 h-10 rounded-lg object-cover border"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-400" />
                    </div>
                  )}
                  {rec.out_photo_url ? (
                    <img
                      src={rec.out_photo_url}
                      alt="OUT"
                      className="w-10 h-10 rounded-lg object-cover border"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
